import React, { useEffect, useState, useRef } from 'react';
import FMCheckboxInput from '../../../common/fm-checkbox-input/fm-checkbox-input';
import './img-spc-mechanical-parts.scss';
import ServiceHandler from '../../../../services/service-handler';
import {
    ImgSpcResultsForDerivativeApiDataData,
    MechanicalPartsCategory,
} from '../../../../models/serice-price-calculator-model';
import { ImgSpcMechanicalPartsContentFragment } from '../../../../models/img-service-price-calculator/img-service-price-calculator';

interface CategoryIcons {
    wiper: string;
    battery: string;
    rotor: string;
    brake: string;
    [key: string]: string | undefined;
}

interface ModelMechanicalPartDetail {
    PartNumber?: string;
    PartName?: string;
    PartCost?: string;
    PartNameThai?: string;
    laborHours?: string;
    FordandMotorcraft?: string;
    ModelYear?: string | null;
}

export interface MechanicalPartsProps {
    onPartSelection?: (isSelected: boolean) => void;
    onMechanicalPartNumbersChange?: (partNumbers: string[]) => void;
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    mechanicalpartsLabel?: ImgSpcMechanicalPartsContentFragment;
}

interface PartItemDisplayProps {
    partName: string;
    partsWithOptions: ModelMechanicalPartDetail[];
    selectedCheckboxes: { [partName: string]: string | undefined };
    handleCheckboxChange: (
        partName: string,
        value: string,
        checked: boolean
    ) => void;
}

const PartItemDisplay: React.FC<PartItemDisplayProps> = ({
    partName,
    partsWithOptions,
    selectedCheckboxes,
    handleCheckboxChange,
}) => {
    return (
        <div className="partItem" key={partName}>
            <div className="categoryItemName">{partName}</div>
            <div className="checkboxContainer">
                {partsWithOptions.map(part => {
                    const optionKey = part.FordandMotorcraft ?? '';
                    const partNumber = part.PartNumber ?? '';

                    if (!optionKey || !partNumber) {
                        console.warn(
                            'Skipping part option due to missing FordandMotorcraft or PartNumber:',
                            part
                        );
                        return null;
                    }

                    const isSelected =
                        selectedCheckboxes[partName] === optionKey;
                    const isDisabledLook =
                        Object.keys(selectedCheckboxes).includes(partName) &&
                        !isSelected;

                    return (
                        <FMCheckboxInput
                            key={partNumber}
                            label={optionKey}
                            labelClassName={`categoryItemCheckbox ${
                                isDisabledLook ? 'checkbox-disabled' : ''
                            }`}
                            value={optionKey}
                            checked={isSelected}
                            onChange={e =>
                                handleCheckboxChange(
                                    partName,
                                    optionKey,
                                    e.target.checked
                                )
                            }
                        />
                    );
                })}
            </div>
        </div>
    );
};

const MechanicalParts = ({
    onPartSelection,
    onMechanicalPartNumbersChange,
    imgSpcDerivativeData,
    mechanicalpartsLabel,
}: MechanicalPartsProps) => {
    const categoryIcons: CategoryIcons = {
        wiper: `${process.env.REACT_APP_AEM_BASE_URL}${mechanicalpartsLabel?.pathToWiperIcon}`,
        battery: `${process.env.REACT_APP_AEM_BASE_URL}${mechanicalpartsLabel?.pathToBatteryIcon}`,
        rotor: `${process.env.REACT_APP_AEM_BASE_URL}${mechanicalpartsLabel?.pathToRotorIcon}`,
        brake: `${process.env.REACT_APP_AEM_BASE_URL}${mechanicalpartsLabel?.pathToBrakeIcon}`,
    };

    const { currentLanguageCode } = ServiceHandler.AppConfigurationService;

    const modelMechanicalPartsData: MechanicalPartsCategory[] | undefined =
        imgSpcDerivativeData?.results?.[0]?.mechanicalParts;

    const [selectedCheckboxes, setSelectedCheckboxes] = useState<{
        [partName: string]: string | undefined;
    }>({});

    const lastSentPartNumbersRef = useRef<string[]>([]);

    const getAllParts = (): ModelMechanicalPartDetail[] => {
        if (!modelMechanicalPartsData) {
            return [];
        }
        return modelMechanicalPartsData.reduce(
            (acc, category) => acc.concat(category.partsdetails || []),
            [] as ModelMechanicalPartDetail[]
        );
    };

    const findPartNumber = (
        allParts: ModelMechanicalPartDetail[],
        partName: string,
        selectedOptionKey: string
    ): string | undefined => {
        const partNameToCompare = (part: ModelMechanicalPartDetail) => {
            const baseName =
                currentLanguageCode.toLocaleLowerCase() === 'th'
                    ? part.PartNameThai ?? part.PartName ?? 'Unknown Part'
                    : part.PartName ?? part.PartNameThai ?? 'Unknown Part';

            return part.ModelYear && part.ModelYear.toLowerCase() !== 'null'
                ? `${baseName} (${part.ModelYear})`
                : baseName;
        };

        const foundPart = allParts.find(
            part =>
                partNameToCompare(part) === partName &&
                part.FordandMotorcraft === selectedOptionKey &&
                part.PartNumber
        );

        if (!foundPart?.PartNumber) {
            console.warn(
                'Could not find PartNumber for selected item:',
                partName,
                '-',
                selectedOptionKey,
                'in provided data'
            );
        }

        return foundPart?.PartNumber;
    };

    const getSelectedPartNumbers = (selectedCheckboxesLocal: {
        [partName: string]: string | undefined;
    }): string[] => {
        const allParts = getAllParts();
        const selectedPartNumbers: string[] = [];

        Object.entries(selectedCheckboxesLocal).forEach(
            ([partName, selectedOptionKey]) => {
                if (selectedOptionKey) {
                    const partNumber = findPartNumber(
                        allParts,
                        partName,
                        selectedOptionKey
                    );
                    if (partNumber) {
                        selectedPartNumbers.push(partNumber);
                    }
                }
            }
        );
        return selectedPartNumbers;
    };

    const hasContentChanged = (
        current: string[],
        lastSent: string[]
    ): boolean => {
        if (current.length !== lastSent.length) return true;

        const sortedCurrent = current
            .slice()
            .sort((a, b) => a.localeCompare(b));
        const sortedLastSent = lastSent
            .slice()
            .sort((a, b) => a.localeCompare(b));

        for (let i = 0; i < sortedCurrent.length; i++) {
            if (sortedCurrent[i] !== sortedLastSent[i]) {
                return true;
            }
        }
        return false;
    };

    const handleCheckboxChange = (
        partName: string,
        value: string,
        checked: boolean
    ) => {
        setSelectedCheckboxes(prev => {
            const newSelection = { ...prev };
            if (checked) {
                if (
                    newSelection[partName] &&
                    newSelection[partName] !== value
                ) {
                    delete newSelection[partName];
                }
                newSelection[partName] = value;
            } else {
                delete newSelection[partName];
            }
            return newSelection;
        });
    };

    useEffect(() => {
        const hasSelectedParts = Object.keys(selectedCheckboxes).length > 0;
        if (onPartSelection) {
            onPartSelection(hasSelectedParts);
        }
    }, [selectedCheckboxes, onPartSelection]);

    useEffect(() => {
        const currentlySelectedPartNumbers = getSelectedPartNumbers(
            selectedCheckboxes
        );

        if (
            hasContentChanged(
                currentlySelectedPartNumbers,
                lastSentPartNumbersRef.current
            )
        ) {
            lastSentPartNumbersRef.current = currentlySelectedPartNumbers;
            if (onMechanicalPartNumbersChange) {
                onMechanicalPartNumbersChange(currentlySelectedPartNumbers);
                console.log('selected', currentlySelectedPartNumbers);
            }
        }
    }, [
        selectedCheckboxes,
        modelMechanicalPartsData,
        currentLanguageCode,
        onMechanicalPartNumbersChange,
    ]);

    if (!modelMechanicalPartsData || modelMechanicalPartsData.length === 0) {
        return null;
    }

    if (
        !modelMechanicalPartsData[0]?.partsdetails ||
        modelMechanicalPartsData[0].partsdetails.length === 0
    ) {
        return <></>;
    }

    return (
        <div className="mechanicalParts">
            <div className="mechanicalPartsTitle">
                {mechanicalpartsLabel?.selectMechanicalPartLabel ??
                    'Select Mechanical Part'}
            </div>

            <div className="categoryItemContainer">
                {modelMechanicalPartsData.map((categoryItem, index) => {
                    const categoryName = (
                        categoryItem.category || ''
                    ).toLowerCase() as keyof CategoryIcons;
                    const IconComponent = categoryIcons[categoryName];

                    const relevantPartsForCategory =
                        categoryItem.partsdetails || [];

                    if (relevantPartsForCategory.length === 0) {
                        return null;
                    }

                    const partsGroupedByName = relevantPartsForCategory.reduce(
                        (
                            acc: { [key: string]: ModelMechanicalPartDetail[] },
                            part
                        ) => {
                            const partNameBase =
                                currentLanguageCode.toLocaleLowerCase() === 'th'
                                    ? part.PartNameThai ??
                                      part.PartName ??
                                      'Unknown Part'
                                    : part.PartName ??
                                      part.PartNameThai ??
                                      'Unknown Part';

                            const partNameToUse =
                                part.ModelYear &&
                                part.ModelYear.toLowerCase() !== 'null'
                                    ? `${partNameBase} (${part.ModelYear})`
                                    : partNameBase;

                            if (
                                partNameToUse &&
                                partNameToUse !== 'Unknown Part'
                            ) {
                                if (!acc[partNameToUse]) {
                                    acc[partNameToUse] = [];
                                }

                                const isDuplicate = acc[partNameToUse].some(
                                    existingPart =>
                                        existingPart.PartNumber ===
                                        part.PartNumber
                                );

                                if (!isDuplicate) {
                                    acc[partNameToUse].push(part);
                                }
                            } else {
                                console.warn(
                                    'Skipping part due to missing name or invalid ModelYear:',
                                    part
                                );
                            }

                            return acc;
                        },
                        {}
                    );

                    return (
                        <div
                            className="categoryItemWrapper"
                            key={`${categoryItem.category}-${index}`}
                        >
                            <div className="categoryItem">
                                <div className="mechanicalLabel">
                                    <div className="icon">
                                        {IconComponent && (
                                            <img
                                                src={IconComponent}
                                                alt={
                                                    categoryItem.category ||
                                                    'Category Icon'
                                                }
                                            />
                                        )}
                                    </div>
                                    <div className="categoryName">
                                        <b>
                                            {currentLanguageCode.toLocaleLowerCase() ===
                                            'th'
                                                ? categoryItem.categoryinthai ??
                                                  categoryItem.category ??
                                                  ''
                                                : categoryItem.category ??
                                                  categoryItem.categoryinthai ??
                                                  ''}
                                        </b>
                                    </div>
                                </div>
                                <div className="categoryDetails">
                                    {Object.entries(partsGroupedByName).map(
                                        ([partName, partsWithOptions]) => (
                                            <PartItemDisplay
                                                key={partName}
                                                partName={partName}
                                                partsWithOptions={
                                                    partsWithOptions
                                                }
                                                selectedCheckboxes={
                                                    selectedCheckboxes
                                                }
                                                handleCheckboxChange={
                                                    handleCheckboxChange
                                                }
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MechanicalParts;
